import PropTypes from "prop-types";
import { DragSource, DropTarget } from "react-dnd";
import { findDOMNode } from "react-dom";
import throttle from "lodash/throttle";

const touchDevice = "ontouchstart" in window || navigator.maxTouchPoints;

const SCROLL_MOVEMENT = touchDevice ? 32 : 20;

const handleMove = throttle((y, sourceHeight) => {
    if (touchDevice) {
        sourceHeight = 36;
    }
    const { height } = document.documentElement.getBoundingClientRect();
    if (sourceHeight + 100 < height) {
        if (y < 60) {
            document.documentElement.scrollTop = document.body.scrollTop =
                (document.documentElement.scrollTop || document.body.scrollTop) - SCROLL_MOVEMENT;
        } else if (y + sourceHeight > height - 50) {
            document.documentElement.scrollTop = document.body.scrollTop =
                (document.documentElement.scrollTop || document.body.scrollTop) + SCROLL_MOVEMENT;
        }
    }
}, 40);

const Source = {
    beginDrag(props, monitor, component) {
        const monitorProps = {
            start: props.index,
            index: props.index,
            add: props.index === -1,
            item: props.item,
            type: props.type,
            viewDef: props.view,
            cart: props.cart,
            catalogEntry: props.catalogEntry,
            activeList: props.activeList,
            fullWidth: props.fullWidth,
            height: findDOMNode(component).getBoundingClientRect().height,
        };

        if (props.onBeginDrag) {
            process.nextTick(() => props.onBeginDrag(monitorProps)); // async to make sure placeholder state appears
        }

        return monitorProps;
    },
    endDrag(props, monitor) {
        if (props.onEndDrag) {
            props.onEndDrag(monitor.getItem());
        }
    },
};

const Target = {
    hover(props, monitor, component) {
        const { height, index, item, type } = monitor.getItem();
        const { y } = monitor.getSourceClientOffset();
        const dragIndex = index;
        const { placeholder, catalogEntry, fullWidth } = props;
        let hoverIndex = props.index;

        handleMove(y, height);

        if (dragIndex !== -1 && placeholder && hoverIndex > 0) {
            hoverIndex = hoverIndex - 1;
        }

        // Don't replace items with themselves
        if (
            dragIndex === hoverIndex ||
            (dragIndex + 1 === hoverIndex &&
                catalogEntry &&
                (catalogEntry.type === "separator" || catalogEntry.type === "image"))
        ) {
            return;
        }

        if (dragIndex !== -1) {
            // Determine rectangle on screen
            const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 4;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 25%
            // When dragging upwards, only move when the cursor is above 25%

            if (!fullWidth && props.item) {
                // items tiles are not 100% width so check
                const hoverClientX = clientOffset.x - hoverBoundingRect.left;
                const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 4;
                // Dragging right
                if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
                    return;
                } else if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX * 3) {
                    // drag left
                    return;
                }
            } else {
                // Dragging downwards
                if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                    return;
                } else if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY * 3) {
                    // drag upwards
                    return;
                }
            }
        }

        // Time to actually perform the action
        if (props.onDragHover) {
            if (dragIndex === -1) {
                props.onDragHover({
                    index: hoverIndex,
                    item: item,
                    type: type,
                });
            } else {
                props.onDragHover({
                    oldIndex: dragIndex,
                    newIndex: hoverIndex,
                });
            }
        }

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        monitor.getItem().index = hoverIndex;
    },
};

function targetCollect(connect) {
    return {
        connectDropTarget: connect.dropTarget(),
    };
}

function sourceCollect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    };
}

export default function (Component) {
    class SortableComponent extends Component {
        render() {
            const { connectDropTarget, connectDragSource, dnd, viewNotes, touchDevice = false } = this.props;

            let content = super.render();
            if (["dropable", "both"].includes(dnd)) {
                content = connectDropTarget(content);
            }

            if (["dragable", "both"].includes(dnd) && !viewNotes && !touchDevice) {
                content = connectDragSource(content);
            }

            return content;
        }
    }

    SortableComponent.contextTypes = {
        ...SortableComponent.contextTypes,
    };

    SortableComponent.propTypes = {
        ...SortableComponent.propTypes,
        index: PropTypes.number,
        dnd: PropTypes.oneOf(["dropable", "dragable", "both", "none"]),
        onMove: PropTypes.func,
    };

    SortableComponent.defaultProps = {
        ...SortableComponent.defaultProps,
        dnd: "both",
    };

    return DropTarget(
        "component",
        Target,
        targetCollect
    )(DragSource("component", Source, sourceCollect)(SortableComponent));
}
